import TaskPrompt from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/TaskActions/TaskPrompt';
import {
  canUseTaskPrompt,
  taskPromptTechnicalContext
} from '../../../../clients/sidebar/utils/taskPrompt';
import React, {
  FC,
  useRef,
  useState,
  MutableRefObject,
  useEffect,
  useLayoutEffect
} from 'react';

import Screenshot, {
  Props as ScreenshotProps
} from '../../../clients/sidebar/components/screenshots';
import { TaskInfo } from './task_info';
import DetailsHead from './details_head';
import DuplicateTasks, { getPendingTaskStatusLabels } from './duplicate_tasks';
import ExpandedScreenshot from './expanded_screenshot';
import { Props as DetailsHeadProps } from './details_head/types';
import { Props as TaskInfoProps } from './task_info/types';
import { Comments as CommentsProps } from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog/types';
import CommentsAndLog from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog';
import camelCaseKeys from 'camelcase-keys';
import cx from 'classnames';
import TitleBar from './details_head/title_bar';
import styles from './index.module.css';
import useMediaQuery from '../../../../clients/sidebar/views/CreateTask/create_task/useMediaQuery';
import useComments from '../../hooks/useComments';
import { useLogs } from '../../hooks/useLogs';
import useScreenshotView from '../../hooks/useScreenshotView';
import TaskDetailTabs, {
  TASK_DETAIL_TABS,
  TaskDetailTabKey
} from '../../../../clients/sidebar/components/TaskDetailTabs';
import { visibleCommentCount } from '../../../../clients/sidebar/components/TaskDetailTabs/visibleCommentCount';
import type { CommentsAndLogSection } from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog';
import Integrations from '../../components/Integrations';
import { WebIntegration } from 'appJS/components/Integrations/types';
import IntegrationLink from '../../../clients/sidebar/views/SidebarPanel/Detailbar/IntegrationLink/IntegrationLink';
import * as translations from './strings';
import { getLangKey } from '../../models/Language';
import type {
  ShortcutConfig,
  TaskDescriptionRichTextBundle
} from '../../../../clients/sidebar/components/RichTextDescription/types';
import type { User as CommentUser } from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog/types';
import { getScreenshotServerUrl } from '../../models/Application';
import { AntdWrapper } from '../../../clients/shared/AntdWrapper';
import {
  ProjectProvider,
  useProjectState
} from '../../../../clients/providers/Project';
import { CurrentUserProvider } from '../../../../clients/providers/CurrentUser';
import {
  OrganizationProvider
} from '../../../../clients/providers/Organization';
import { Modal, message } from 'antd';
import TaskActions from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/TaskActions';
import ReportedBy from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/ReportedBy';
import usePusherSubscribe from 'appJS/hooks/usePusherSubscribe';
import { Event, Subscription } from 'appJS/models/Pusher';
import { NewStateUpdateBulkRequestArgs } from 'appJS/models/BulkRequest';
import { post } from '../../../../redesign/javascript/utils/fetch';
import SessionReplay from '../../../../clients/shared/SessionReplay';
import {
  getData,
  setData,
  TASK_DETAILS_ACTIVE_TAB
} from '../../models/LocalStorage';
import {
  clampDetailsPaneWidth,
  getStoredDetailsPaneWidth,
  setStoredDetailsPaneWidth
} from './detailsPaneWidth';
import {
  getStoredExpandedTaskView,
  setStoredExpandedTaskView
} from './expandedTaskView';

const strings = translations[getLangKey()];

const getStoredActiveTab = (): TaskDetailTabKey => {
  const storedTab = getData(TASK_DETAILS_ACTIVE_TAB);

  return TASK_DETAIL_TABS.includes(storedTab) ? storedTab : 'details';
};

interface Props {
  detailsHead: DetailsHeadProps;
  taskInfo: TaskInfoProps;
  screenshot: ScreenshotProps;
  comments: CommentsProps;
  integrations: any;
  open: boolean;
  closeModal: () => void;
  openModal: () => void;
  releaseExpandedView: () => void;
  taskOpenToken?: number;
}

interface DetailsProps {
  screenshot: any;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  container: () => HTMLElement;
  destinations: Array<{
    id: string | number;
    name: string;
    status_id: number | null;
  }>;
  currentTask: {
    id: number;
    local_task_id: number;
    title?: string;
    description?: string;
    status?: number;
    priority_id?: number;
    created_at?: string;
    requester_display?: string;
    status_label?: string;
  };
  isCroppedView: boolean;
  handleScreenshotToggle: (checked: boolean) => void;
  handleRegenerateScreenshot: (props: any) => Promise<void>;
  bugherdUrl: string;
  open: boolean;
  createdAt: string;
  reportedProps: any;
  fullstorySessionUrl?: string;
  logrocketSessionUrl?: string;
  taskInfo: TaskInfoProps;
  isRecording: boolean;
  currentUser: any;
  duplicateTasks: any[];
  isRefreshingDuplicateTasks: boolean;
  onDuplicateTaskBulkUpdateStart: (
    payload?: Pick<
      NewStateUpdateBulkRequestArgs,
      'taskIds' | 'targetState' | 'source'
    >
  ) => void;
  onDuplicateTaskBulkUpdateSuccess: () => Promise<void>;
  onDuplicateTaskBulkUpdateError: () => void;
  setActiveTask: (taskId: number) => void;
  onAttachmentUploaded: (attachment: { name: string; url: string }) => void;
}

function normalizeTagForShortcut(tag: {
  get?: (k: string) => unknown;
  id?: number;
  name?: string;
}) {
  return typeof tag.get === 'function'
    ? { id: tag.get('id') as number, display: String(tag.get('name') ?? '') }
    : { id: tag.id as number, display: String(tag.name ?? '') };
}

type BuildShortcutConfigArgs = {
  canEditDescription: boolean;
  taskInfo: TaskInfoProps;
  users: CommentUser[];
  column: DetailsHeadProps['column'];
  taskEditStatus: boolean;
  destinations: DetailsHeadProps['destinations'];
  markAsStatus: DetailsHeadProps['markAsStatus'];
  mentionableTasks: ShortcutConfig['mentionableTasks'];
};

function buildShortcutConfig({
  canEditDescription,
  taskInfo,
  users,
  column,
  taskEditStatus,
  destinations,
  markAsStatus,
  mentionableTasks
}: BuildShortcutConfigArgs): ShortcutConfig | null {
  if (!canEditDescription) return null;

  return {
    assignees: taskInfo.assignees.map((a: { id: number }) => a.id),
    canEditAssignees: taskInfo.editAssignees,
    assignableUsers: users.filter(
      ({ role, is_on_project }) => role !== 'collaborator' && is_on_project
    ),
    onAssigneesChange: (assigneeIds: number[]) =>
      taskInfo.setProperty({ assignee_ids: assigneeIds }),
    watcherIds: taskInfo.watcherIds ?? [],
    onWatchersChange: (watcherIds: number[]) =>
      taskInfo.setProperty({ watcher_ids: watcherIds }),
    guestVisible: !!taskInfo.guestVisible,
    status: column?.status_id ?? column?.id,
    canEditStatus: taskEditStatus,
    onStatusChange: (columnId: number) => {
      const col = destinations.find(
        d =>
          d.id === columnId ||
          d.status_id === columnId ||
          Number(d.id) === columnId
      );
      if (col) (markAsStatus as (c: (typeof destinations)[0]) => void)(col);
    },
    columns: destinations.map(d => ({
      id: typeof d.id === 'number' ? d.id : Number(d.id) || 0,
      name: d.name,
      position: d.position ?? 0,
      color: '',
      status_id: null
    })),
    tags: taskInfo.tagIds
      .map(id =>
        taskInfo.allTags.map(normalizeTagForShortcut).find(t => t.id === id)
      )
      .filter((t): t is { id: number; display: string } => t != null),
    canEditTags: taskInfo.editTags,
    onTagsChange: newTags =>
      taskInfo.setProperty({
        tag_ids: newTags.map(t => t.id),
        tag_names: newTags.map(t => t.display)
      }),
    availableTags: taskInfo.allTags.map(normalizeTagForShortcut),
    severity: taskInfo.priorityId,
    canEditSeverity: taskInfo.editSeverity,
    onSeverityChange: (severity: number) =>
      taskInfo.setProperty({ priority_id: severity }),
    mentionableTasks
  } as ShortcutConfig;
}

async function getDuplicateTasks(projectId, taskId) {
  try {
    const response = await fetch(
      `/projects/${projectId}/tasks/${taskId}/find_duplicates`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      }
    );

    if (response.status === 204) {
      return { duplicates: [] };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.log('Failed to fetch duplicate tasks:', error);
    return null;
  }
}

const Details: FC<DetailsProps> = ({
  screenshot,
  videoRef,
  container,
  destinations,
  currentTask,
  isCroppedView,
  handleScreenshotToggle,
  handleRegenerateScreenshot,
  bugherdUrl,
  open,
  createdAt,
  reportedProps,
  fullstorySessionUrl,
  logrocketSessionUrl,
  taskInfo,
  isRecording,
  currentUser,
  duplicateTasks,
  isRefreshingDuplicateTasks,
  onDuplicateTaskBulkUpdateStart,
  onDuplicateTaskBulkUpdateSuccess,
  onDuplicateTaskBulkUpdateError,
  setActiveTask,
  onAttachmentUploaded
}) => (
  <div
    className={styles.detailsMiddle}
    onScroll={event => event.stopPropagation()}
  >
    {currentUser.role !== 'guest' && (
      <DuplicateTasks
        duplicateTasks={duplicateTasks}
        container={container}
        projectId={taskInfo.projectId}
        destinations={destinations}
        currentTask={currentTask}
        isUpdatingTasks={isRefreshingDuplicateTasks}
        onBulkUpdateStart={onDuplicateTaskBulkUpdateStart}
        onBulkUpdateSuccess={onDuplicateTaskBulkUpdateSuccess}
        onBulkUpdateError={onDuplicateTaskBulkUpdateError}
        setActiveTask={setActiveTask}
      />
    )}

    {!open && (
      <div className={styles.screenshotWrapper}>
        <Screenshot
          {...Object.assign(screenshot, { videoRef })}
          isCroppedView={isCroppedView}
          handleScreenshotToggle={(checked: boolean) =>
            handleScreenshotToggle(checked)
          }
          handleRegenerateScreenshot={(props: any) =>
            handleRegenerateScreenshot(props)
          }
          apiDomain={bugherdUrl}
          screenshotServerUrl={getScreenshotServerUrl()}
        />
      </div>
    )}
    {fullstorySessionUrl && (
      <IntegrationLink
        title={strings.fullStoryTitle}
        url={fullstorySessionUrl}
        linkText={strings.viewSession}
        titleIcon={
          <span className={cx(styles.integrationLogo, styles.fullStoryLogo)} />
        }
      />
    )}
    {logrocketSessionUrl && (
      <IntegrationLink
        title={strings.logRocketTitle}
        url={logrocketSessionUrl}
        linkText={strings.viewSession}
        titleIcon={
          <span className={cx(styles.integrationLogo, styles.logRocketLogo)} />
        }
      />
    )}
    <ReportedBy
      asFields
      createdAt={createdAt}
      email={reportedProps.email}
      display={reportedProps.display}
    />
    <TaskInfo
      {...Object.assign(taskInfo, {
        isRecording,
        currentUser,
        onAttachmentUploaded
      })}
    />
  </div>
);

const TaskDetails: FC<
  Props & {
    activeTab: TaskDetailTabKey;
    onTabChange: (tab: TaskDetailTabKey) => void;
  }
> = props => {
  const {
    detailsHead,
    taskInfo,
    screenshot,
    open,
    closeModal,
    openModal,
    releaseExpandedView,
    activeTab,
    onTabChange
  } = props;
  const outerRef: MutableRefObject<null | HTMLDivElement> = useRef(null);
  const modalContentRef: MutableRefObject<null | HTMLDivElement> = useRef(null);
  const videoRef: MutableRefObject<null | HTMLVideoElement> = useRef(null);
  const useParentWindow: boolean = true;
  const isSmallView = useMediaQuery(
    '(max-width: 1360px) and (min-width: 950px)'
  );
  const isMobileView = useMediaQuery('(max-width: 475px)', useParentWindow);
  const [taskLogTaskId, setTaskLogTaskId] = useState<number | null>(null);
  const [detailsPaneWidth, setDetailsPaneWidth] = useState(
    getStoredDetailsPaneWidth
  );
  const detailsPaneWidthRef = useRef(detailsPaneWidth);
  const [modalContentWidth, setModalContentWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [uploadedAttachment, setUploadedAttachment] = useState<{
    name: string;
    url: string;
    uploadedAt: number;
    taskId: number;
  } | null>(null);

  const {
    column,
    localTaskId,
    projectId,
    projectName,
    canViewTaskBoard,
    isAdminView,
    creator,
    createdAt,
    title,
    description,
    canEditDescription,
    canMoveToProject,
    canDeleteTask,
    editStatus,
    destinations,
    secretId,
    markAsStatus,
    projects,
    moveToProject,
    deleteTask,
    pusherApiKey,
    pusherChannelName
  } = detailsHead;

  const [taskEditStatus, setTaskEditStatus] = useState<boolean>(editStatus);
  const [duplicateTasks, setDuplicateTasks] = useState<any[]>([]);
  const { project } = useProjectState();
  const hasDuplicateTasks = project?.duplicateTasks;
  const [isRefreshingDuplicateTasks, setIsRefreshingDuplicateTasks] = useState<
    boolean
  >(false);
  const reportedProps =
    typeof creator === 'object' && creator !== null
      ? creator
      : { display: creator };

  const refreshDuplicateTasks = async () => {
    const data = await getDuplicateTasks(taskInfo.projectId, taskInfo.taskId);

    if (!data) {
      return false;
    }

    setDuplicateTasks(data.duplicates || []);

    return true;
  };

  useEffect(() => {
    setDuplicateTasks([]);

    if (!hasDuplicateTasks) {
      return;
    }

    void refreshDuplicateTasks();
  }, [taskInfo.projectId, taskInfo.taskId, hasDuplicateTasks]);

  const currentTask = {
    id: taskInfo.taskId,
    local_task_id: localTaskId,
    title,
    description,
    status: column?.status_id,
    priority_id: taskInfo.priorityId,
    created_at: createdAt,
    requester_display: reportedProps.display || 'Anonymous',
    status_label: column?.name,
    assignees: taskInfo.assignees
  };
  const [currentTaskData, setCurrentTaskData] = useState(currentTask);

  useEffect(() => {
    setCurrentTaskData(currentTask);
  }, [
    taskInfo.taskId,
    localTaskId,
    title,
    description,
    column?.status_id,
    column?.name,
    taskInfo.priorityId,
    createdAt,
    reportedProps.display,
    taskInfo.assignees
  ]);

  const handleDuplicateTaskBulkUpdateStart = (
    payload?: Pick<
      NewStateUpdateBulkRequestArgs,
      'taskIds' | 'targetState' | 'source'
    >
  ) => {
    setIsRefreshingDuplicateTasks(true);

    if (!payload) {
      return;
    }

    const pendingStatusLabels = getPendingTaskStatusLabels({
      taskIds: payload.taskIds,
      destinations,
      targetState: payload.targetState
    });

    if (Object.keys(pendingStatusLabels).length === 0) {
      return;
    }

    setCurrentTaskData(task =>
      pendingStatusLabels[task.id]
        ? {
            ...task,
            status_label: pendingStatusLabels[task.id]
          }
        : task
    );
    setDuplicateTasks(tasks =>
      tasks.map(task =>
        pendingStatusLabels[task.id]
          ? {
              ...task,
              status_label: pendingStatusLabels[task.id]
            }
          : task
      )
    );
  };

  const handleDuplicateTaskBulkUpdateError = () => {
    setIsRefreshingDuplicateTasks(false);
  };

  const handleDuplicateTaskBulkUpdateSuccess = async () => {
    const didRefresh = await refreshDuplicateTasks();

    setIsRefreshingDuplicateTasks(false);

    if (didRefresh) {
      message.success('Tasks have been updated');
    }
  };

  const {
    container,
    currentUser,
    users,
    usersNotOnProject,
    guestVisible,
    taskId,
    canEditComment,
    canViewPrivateComments,
    canCreatePrivateComments,
    canViewCommitComment,
    billingRights,
    owner,
    orgId,
    bugherdUrl,
    assignGuestsAllowed,
    assignGuests,
    setActiveTask,
    mentionableTasks
  } = props.comments;

  const {
    connectedIntegrations,
    harvestIntegration,
    fullstorySessionUrl,
    logrocketSessionUrl,
    sessionReplayAvailable,
    taskConnectionData
  } = props.integrations;

  const {
    screenshotData: { url }
  } = screenshot;

  const toggleExpandedView = () => {
    if (!open) {
      openModal();
    } else {
      closeModal();
    }
  };

  const closeDetailBar = () => {
    releaseExpandedView();
    detailsHead.closeDetailBar();
    videoRef?.current?.pause();
  };

  const canViewTaskBoardLink = canViewTaskBoard && !isAdminView;

  const isRecording: boolean =
    !!url && (url.endsWith('.webm') || url.endsWith('.mp4'));

  const hasSelector = !(
    typeof taskInfo.selector !== 'object' || !taskInfo.selector.path
  );

  const {
    createComment,
    deleteComment,
    updateComment,
    comments,
    loading: commentsLoading,
    handlePusherUpdateForLocalComment
  } = useComments({
    taskId,
    projectId,
    bugherdUrl,
    currentUser
  });

  const { addLog, loading: logsLoading, logs } = useLogs({
    taskId,
    projectId,
    bugherdUrl,
    enabled: taskLogTaskId === taskId
  });

  const requestTaskLogs = () => setTaskLogTaskId(taskId);

  const handleAttachmentUploaded = ({
    name,
    url
  }: {
    name: string;
    url: string;
  }) =>
    setUploadedAttachment({
      name,
      url,
      uploadedAt: Date.now(),
      taskId
    });

  const activeTaskUploadedAttachment =
    uploadedAttachment?.taskId === taskId ? uploadedAttachment : null;

  const {
    isCroppedView,
    handleScreenshotToggle,
    handleRegenerateScreenshot
  } = useScreenshotView({
    userId: currentUser.id,
    projectId,
    organizationId: orgId,
    hasSelector,
    projectDetailsUrl: screenshot.projectDetailsUrl,
    onRegenerateScreenshot: () => {
      taskInfo.setProperty({
        retry_screenshot_at: new Date().toISOString()
      });
    }
  });

  const webIntegrations: WebIntegration[] = [];

  if (fullstorySessionUrl) {
    webIntegrations.push({
      key: 'fullStory',
      integration: {
        id: 'View session',
        url: fullstorySessionUrl
      }
    });
  }

  if (logrocketSessionUrl) {
    webIntegrations.push({
      key: 'logRocket',
      integration: {
        id: 'View session',
        url: logrocketSessionUrl
      }
    });
  }

  const handleGuestEditTaskStatusUpdate = (
    isGuestUser: boolean,
    pusherData: any
  ) => {
    if (isGuestUser) {
      setTaskEditStatus(pusherData.guest_edit_task_status);
    }
  };

  usePusherSubscribe({
    pusher: {
      config: {
        pusherApiKey,
        pusherChannelAuthEndpoint: '/pusher/auth'
      },
      projects: [
        {
          id: String(projectId),
          pusherChannelName
        }
      ],
      events: [
        {
          name: Event.GUEST_EDIT_STATUS_UPDATED,
          onUpdate: handleGuestEditTaskStatusUpdate.bind(
            null,
            currentUser.role === 'guest'
          )
        },
        {
          name: Event.TASK_UPDATE,
          onUpdate: ({ task, task_log }) => {
            if (task?.id !== taskId) return;
            task_log = task_log
              ? camelCaseKeys(task_log, { deep: true })
              : undefined;
            const nextTask = camelCaseKeys(task, { deep: true });
            const isComment = task_log?.changesHtml?.includes('comment');
            if (isComment) return handlePusherUpdateForLocalComment(task_log);
            setCurrentTaskData(currentTask => ({
              ...currentTask,
              title: nextTask.title ?? currentTask.title,
              description: nextTask.description ?? currentTask.description,
              status_label: task_log?.status_name || currentTask.status_label,
              assignees: nextTask.assignees || currentTask.assignees
            }));
            addLog(task_log);
          }
        },
        {
          name: Event.BULK_ACTION_COMPLETED,
          onUpdate: ({ payload }) => {
            handleDuplicateTaskBulkUpdateSuccess();
          }
        }
      ],
      subscription: Subscription.TASK_ACTIONS
    },
    loading: false
  });

  const harvestEnabled = !!harvestIntegration?.id;

  const showDetailsTab = !!(
    currentUser.role !== 'guest' &&
    (connectedIntegrations.length ||
      webIntegrations.length ||
      harvestEnabled)
  );

  const harvestEmbedUrl = `https://platform.harvestapp.com/platform/timer?app_name=BugHerd&closable=false&chromeless=true&permalink=${bugherdUrl}/projects/${projectId}/tasks/${localTaskId}&external_item_id=${localTaskId}&external_item_name=${`Task ${localTaskId}`}&external_group_id=${projectId}&external_group_name=${projectName}`;

  const resolvedTab: TaskDetailTabKey =
    (activeTab === 'integrations' && !showDetailsTab) ||
    (activeTab === 'session' && !sessionReplayAvailable)
      ? 'details'
      : activeTab;

  useEffect(() => {
    if (resolvedTab === 'comments') requestTaskLogs();
  }, [resolvedTab, taskId]);

  const taskPromptActions = (
    <TaskPrompt
      container={container}
      context={
        canUseTaskPrompt(currentUser.role)
          ? {
              projectId,
              projectName,
              taskNumber: localTaskId,
              bugherdUrl,
              title:
                currentTaskData.id === taskId ? currentTaskData.title : title,
              description:
                currentTaskData.id === taskId
                  ? currentTaskData.description
                  : description,
              originalCopyText: detailsHead.originalCopyText,
              pageUrl: taskInfo.taskUrls?.absoluteUrl,
              screenshotUrl: screenshot.screenshotData?.url,
              isRecording,
              attachments: taskInfo.attachmentsDisabled
                ? []
                : taskInfo.attachments,
              ...taskPromptTechnicalContext(
                taskInfo.browserData,
                taskInfo.selector,
                taskInfo.viewSelector
              )
            }
          : undefined
      }
    />
  );

  const TaskActionsComponent = () => (
    <TaskActions
      taskMoveToProject={canMoveToProject}
      taskDelete={
        canDeleteTask &&
        (currentUser.role !== 'guest' ||
          (typeof creator !== 'string' && currentUser.id === creator?.id))
      }
      taskEditStatus={taskEditStatus}
      container={container}
      // @ts-ignore
      columns={destinations}
      // @ts-ignore
      column={column}
      taskId={taskId}
      projectId={projectId}
      shareUrl={`${bugherdUrl}/t/${secretId}`}
      updateTask={markAsStatus}
      isAdminView={isAdminView}
      hideDivider
      isSmallDetailsView={false}
      // @ts-ignore
      moveToProject={async (newProjectId: number, domEvent: any) => {
        closeDetailBar();
        window.bugherd.applicationView.showKanban(domEvent);
        const response = await post(
          `${bugherdUrl}/projects/${projectId}/tasks/move`,
          {
            destination_project_id: newProjectId,
            id: taskId
          }
        );

        if (response.ok) {
          message.success('Task moved successfully');
        } else {
          message.error('Failed to move task, please try again');
        }
      }}
      // @ts-ignore
      deleteTask={deleteTask}
    />
  );

  const mapCommentUser = (u: CommentUser) => ({
    id: u.id,
    email: u.email,
    name: u.name
  });

  const taskDescriptionRichText: TaskDescriptionRichTextBundle = {
    mentionPreviewContext: {
      descriptionMarkdown: description,
      projectMembers: users
        .filter(({ role }) => role !== 'guest')
        .map(mapCommentUser),
      projectUsers: users.map(mapCommentUser),
      allUsers: [...users, ...(usersNotOnProject ?? [])].map(mapCommentUser),
      includeGuests: assignGuestsAllowed && assignGuests,
      mentionableTasks,
      setActiveTask,
      bugherdUrl,
      projectId
    },
    shortcutConfig: buildShortcutConfig({
      canEditDescription,
      taskInfo,
      users,
      column,
      taskEditStatus,
      destinations,
      markAsStatus,
      mentionableTasks
    })
  };

  const TaskDetailsHeadComponent = () => (
    <DetailsHead
      {...Object.assign(detailsHead, {
        className: styles.detailsHead,
        key: taskId,
        showTaskActions: true,
        taskActions: TaskActionsComponent,
        taskPromptActions,
        taskDescriptionRichText,
        isExpandedView: false
      })}
    />
  );

  const renderComments = (section: CommentsAndLogSection) => (
    <div className={styles.eventLogAndComments}>
      <CommentsAndLog
        container={document.body}
        commentsLoading={!!commentsLoading}
        comments={comments}
        currentUserId={currentUser.id}
        // @ts-ignore
        users={camelCaseKeys(
          users.filter(user => !!user.is_on_project),
          { deep: true }
        )}
        // @ts-ignore
        usersNotOnProject={camelCaseKeys(
          users.filter(user => !user.is_on_project),
          { deep: true }
        )}
        // @ts-ignore
        logs={logs}
        logsLoading={logsLoading}
        taskId={taskId}
        canEditComment={canEditComment}
        canViewPrivateComments={canViewPrivateComments}
        canCreatePrivateComments={canCreatePrivateComments}
        canViewCommitComment={canViewCommitComment}
        billingRights={billingRights}
        // @ts-ignore
        owner={owner}
        bugherdUrl={bugherdUrl}
        organizationId={orgId}
        guestVisible={!!guestVisible}
        createComment={createComment}
        deleteComment={deleteComment}
        updateComment={updateComment}
        isSmallView={isSmallView}
        isAdminView={isAdminView}
        projectId={projectId}
        apiDomain={bugherdUrl}
        assignGuestsAllowed={assignGuestsAllowed}
        assignGuests={assignGuests}
        assignees={taskInfo.assignees}
        setActiveTask={setActiveTask}
        mentionableTasks={mentionableTasks}
        onLogTabOpen={requestTaskLogs}
        section={section}
        uploadedAttachment={activeTaskUploadedAttachment}
      />
    </div>
  );

  const renderIntegrations = () => (
    <div className={styles.integrationsWrapper}>
      <Integrations
        connectedIntegrations={camelCaseKeys(connectedIntegrations, {
          deep: true
        })}
        harvestIntegration={harvestIntegration}
        harvestEmbedUrl={harvestEmbedUrl}
        container={container}
        projectId={projectId}
        bugherdUrl={bugherdUrl}
        isAdminView={isAdminView}
        taskId={taskId}
        taskLocalId={localTaskId}
        isSmallView={isSmallView}
        webIntegrations={webIntegrations}
        organizationId={orgId}
        taskConnectionData={camelCaseKeys(taskConnectionData, { deep: true })}
      />
    </div>
  );

  const renderTabPanel = () => {
    if (resolvedTab === 'comments') return renderComments('comments');
    if (resolvedTab === 'integrations') return renderIntegrations();
    if (resolvedTab === 'session') {
      return (
        <div className={styles.sessionTab}>
          <SessionReplay
            available={sessionReplayAvailable}
            apiDomain={bugherdUrl}
            projectId={taskInfo.projectId}
            taskId={taskInfo.taskId}
          />
        </div>
      );
    }

    return (
      <Details
        screenshot={screenshot}
        videoRef={videoRef}
        container={container}
        destinations={destinations}
        currentTask={currentTaskData}
        isCroppedView={isCroppedView}
        handleScreenshotToggle={handleScreenshotToggle}
        handleRegenerateScreenshot={handleRegenerateScreenshot}
        bugherdUrl={bugherdUrl}
        open={open}
        createdAt={createdAt}
        reportedProps={reportedProps}
        fullstorySessionUrl={fullstorySessionUrl}
        logrocketSessionUrl={logrocketSessionUrl}
        taskInfo={taskInfo}
        isRecording={isRecording}
        currentUser={currentUser}
        duplicateTasks={duplicateTasks}
        isRefreshingDuplicateTasks={isRefreshingDuplicateTasks}
        onDuplicateTaskBulkUpdateStart={handleDuplicateTaskBulkUpdateStart}
        onDuplicateTaskBulkUpdateSuccess={handleDuplicateTaskBulkUpdateSuccess}
        onDuplicateTaskBulkUpdateError={handleDuplicateTaskBulkUpdateError}
        setActiveTask={setActiveTask}
        onAttachmentUploaded={handleAttachmentUploaded}
      />
    );
  };

  const renderTabbedBody = () => (
    <>
      <TaskDetailTabs
        activeKey={resolvedTab}
        onChange={onTabChange}
        commentCount={visibleCommentCount(
          comments,
          canViewPrivateComments,
          canViewCommitComment
        )}
        showSession={!!sessionReplayAvailable}
        showIntegrations={showDetailsTab}
      />
      <div className={styles.tabContent}>{renderTabPanel()}</div>
    </>
  );

  const titleBar = (
    <TitleBar
      key={`${taskId}-titlebar`}
      closeDetailBar={closeDetailBar}
      toggleExpandedView={toggleExpandedView}
      column={column}
      localTaskId={localTaskId}
      projectId={projectId}
      url={detailsHead.url}
      priorityId={taskInfo.priorityId}
      isExpandedView={open}
      canViewTaskBoardLink={canViewTaskBoardLink}
    />
  );

  const renderedDetailsPaneWidth = modalContentWidth
    ? clampDetailsPaneWidth(detailsPaneWidth, modalContentWidth)
    : detailsPaneWidth;

  const updateDetailsPaneWidthFromClientX = (clientX: number) => {
    const container = modalContentRef.current;
    if (!container) return;

    const { left, width } = container.getBoundingClientRect();
    const nextWidth = clampDetailsPaneWidth(width - (clientX - left), width);
    setModalContentWidth(width);
    detailsPaneWidthRef.current = nextWidth;
    setDetailsPaneWidth(nextWidth);
  };

  const handleDividerPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
    updateDetailsPaneWidthFromClientX(event.clientX);
  };

  const handleDividerPointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;

    updateDetailsPaneWidthFromClientX(event.clientX);
  };

  const handleDividerPointerUp = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;

    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsResizing(false);
    setStoredDetailsPaneWidth(detailsPaneWidthRef.current);
  };

  useEffect(() => {
    if (!open) return;

    const syncModalContentWidth = () => {
      const container = modalContentRef.current;
      if (!container) return;

      setModalContentWidth(container.getBoundingClientRect().width);
    };

    syncModalContentWidth();
    window.addEventListener('resize', syncModalContentWidth);

    return () => window.removeEventListener('resize', syncModalContentWidth);
  }, [open]);

  return (
    <div className={cx(styles.taskDetailsWrapper, { mobile: isMobileView })}>
      {open ? (
        <div
          className={cx(styles.modalContent, {
            [styles.isResizing]: isResizing
          })}
          ref={modalContentRef}
          style={
            {
              '--details-pane-width': `${renderedDetailsPaneWidth}px`
            } as React.CSSProperties
          }
        >
          <div className={styles.leftPane}>
            <ExpandedScreenshot
              screenshot={screenshot}
              videoRef={videoRef}
              bugherdUrl={bugherdUrl}
              sessionReplayAvailable={!!sessionReplayAvailable}
              handleRegenerateScreenshot={handleRegenerateScreenshot}
            />
          </div>
          <div
            className={styles.divider}
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={Math.round(renderedDetailsPaneWidth)}
            onPointerDown={handleDividerPointerDown}
            onPointerMove={handleDividerPointerMove}
            onPointerUp={handleDividerPointerUp}
            onPointerCancel={handleDividerPointerUp}
          />
          <div className={styles.rightPane}>
            {titleBar}
            {TaskDetailsHeadComponent()}
            {renderTabbedBody()}
          </div>
        </div>
      ) : (
        <>
          {titleBar}
          <div className={styles.container} ref={outerRef}>
            {TaskDetailsHeadComponent()}
            {renderTabbedBody()}
          </div>
        </>
      )}
    </div>
  );
};

export default (props: Props) => {
  const isDesktopView = useMediaQuery('(min-width: 980px)');
  const taskOpenToken = props.taskOpenToken ?? 0;
  const [open, setModal] = useState<boolean>(
    () => isDesktopView && getStoredExpandedTaskView()
  );
  const [seenTaskOpenToken, setSeenTaskOpenToken] = useState(taskOpenToken);
  const [activeTab, setActiveTab] = useState<TaskDetailTabKey>(
    getStoredActiveTab
  );
  const paneRightRef = useRef<HTMLDivElement>(null);
  const paneCenterRef = useRef<HTMLDivElement>(null);

  if (taskOpenToken !== seenTaskOpenToken) {
    setSeenTaskOpenToken(taskOpenToken);
    setModal(isDesktopView && getStoredExpandedTaskView());
  }

  const syncExpandedPaneClasses = (expanded: boolean) => {
    paneRightRef.current = document.querySelector(
      '.pane-right'
    ) as HTMLDivElement;
    paneCenterRef.current = document.querySelector(
      '.pane-center'
    ) as HTMLDivElement;
    paneRightRef.current?.classList.toggle(styles.paneRightExpanded, expanded);
    paneCenterRef.current?.classList.toggle(
      styles.paneCenterExpanded,
      expanded
    );
  };

  useLayoutEffect(() => {
    document.body.classList.add('antd-container');
    syncExpandedPaneClasses(open && isDesktopView);
  }, [open, isDesktopView]);

  const handleTabChange = (tab: TaskDetailTabKey) => {
    setActiveTab(tab);
    setData(TASK_DETAILS_ACTIVE_TAB, tab);
  };

  const closeModal = () => {
    setStoredExpandedTaskView(false);
    setModal(false);
    syncExpandedPaneClasses(false);
  };

  const openModal = () => {
    setStoredExpandedTaskView(true);
    setModal(true);
    syncExpandedPaneClasses(true);
  };

  const releaseExpandedView = () => {
    setModal(false);
    syncExpandedPaneClasses(false);
  };

  const { bugherdUrl } = props.comments;
  const { projectId } = props.detailsHead;

  return (
    <AntdWrapper>
      <CurrentUserProvider apiDomain={bugherdUrl}>
        <ProjectProvider apiDomain={bugherdUrl} id={`${projectId}`}>
          <OrganizationProvider
            apiDomain={bugherdUrl}
            id={props.comments.orgId}
          >
            {open && isDesktopView ? (
              <Modal
                open
                closable={false}
                getContainer={props.detailsHead.container}
                onCancel={closeModal}
                title={null}
                footer={null}
                centered
                className={styles.modal}
              >
                <TaskDetails
                  {...props}
                  open
                  closeModal={closeModal}
                  openModal={openModal}
                  releaseExpandedView={releaseExpandedView}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </Modal>
            ) : (
              <TaskDetails
                {...props}
                open={false}
                openModal={openModal}
                closeModal={closeModal}
                releaseExpandedView={releaseExpandedView}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            )}
          </OrganizationProvider>
        </ProjectProvider>
      </CurrentUserProvider>
    </AntdWrapper>
  );
};
